/**
 * Host detection helpers.
 *
 * Subdomain routing:
 *   - finance.hibane.ai serves the Finance Desk at the root path.
 *     Login is /login, dashboard is /dashboard (no /finance prefix).
 *   - hibane.ai/finance/* still works for backward compatibility and
 *     redirects to the subdomain when running in production.
 *
 * desk.hibane.ai / hr.hibane.ai are not yet active for routing.
 */

export const DESK_HOST = 'desk.hibane.ai';
export const HR_HOST = 'hr.hibane.ai';
export const FINANCE_HOST = 'finance.hibane.ai';
const MARKETING_HOSTS = new Set(['hibane.ai', 'www.hibane.ai']);

const normalizeHostname = (hostname: string): string => {
  return hostname.trim().toLowerCase().replace(/\.$/, '');
};

const getHostname = (): string => {
  if (typeof window === 'undefined') return '';
  return normalizeHostname(window.location.hostname);
};

// Desk/HR subdomain routing remains disabled until ready.
export const isDeskHost = (): boolean => false;
export const isHrHost = (): boolean => false;

export const isFinanceHost = (): boolean => {
  const hostname = getHostname();
  return hostname === FINANCE_HOST || hostname === `www.${FINANCE_HOST}`;
};

export const isMarketingHost = (): boolean => MARKETING_HOSTS.has(getHostname());

/**
 * True for the Lovable editor preview, the published *.lovable.app domain,
 * or local development.
 */
export const isPreviewOrDevHost = (): boolean => {
  const h = getHostname();
  if (!h) return true;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.endsWith('.lovable.app') ||
    h.endsWith('.lovableproject.com') ||
    h.endsWith('.lovable.dev')
  );
};

export const deskUrl = (path: string = '/admin/dashboard'): string => {
  return path.startsWith('/') ? path : `/${path}`;
};

export const hrUrl = (path: string = '/hr/dashboard'): string => {
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Convert a `/finance/...` style path to the right URL for the current host.
 *
 * On the finance subdomain (finance.hibane.ai), the path is rewritten to drop
 * the `/finance` prefix (so `/finance/dashboard` -> `/dashboard`).
 * On the marketing host (hibane.ai), it returns an absolute URL pointing to
 * the finance subdomain so cross-portal links go to the right place.
 * Everywhere else (preview, localhost), it returns the same-origin path.
 */
export const financeUrl = (path: string = '/finance/dashboard'): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const stripped = normalized.replace(/^\/finance(?=\/|$)/, '') || '/';

  if (isFinanceHost()) {
    return stripped === '' ? '/' : stripped;
  }
  if (isMarketingHost()) {
    return `https://${FINANCE_HOST}${stripped === '' ? '/' : stripped}`;
  }
  // Preview / dev / other hosts: keep legacy /finance/* paths same-origin.
  return normalized;
};

/**
 * Map a URL or pathname produced for the legacy `/finance/...` scheme to the
 * pathname the router should navigate to on the current host. Used by
 * notification deep-link handlers and the login post-auth redirect.
 */
export const financeRouterPath = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (isFinanceHost()) {
    const stripped = normalized.replace(/^\/finance(?=\/|$)/, '') || '/';
    return stripped === '' ? '/' : stripped;
  }
  return normalized;
};
